import React, {useRef, useState} from 'react';
import {
	Button,
	ButtonDropdownField,
	DropdownList,
	FlexColumn,
	FlexContainer,
	FlexRow,
	Icon,
	IconTooltip,
	Label,
	Modal,
	Text,
	TextField,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import Util from '../../../forecast-app/shared/util/util';
import UpdateClientMutation from '../../../mutations/update_client_mutation';
import {getDefaultMediumClientLogo} from '../../../components/default_avatars';
import {BUTTON_COLOR, BUTTON_STYLE, isoCountries, MODULE_TYPES} from '../../../constants';
import {createFragmentContainer, graphql} from 'react-relay';
import Warning from '../../../components/warning';
import type {EditClientDetailsModal_viewer$data as EditClientDetailsModal_viewer} from './__generated__/EditClientDetailsModal_viewer.graphql';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {showChangesSavedToast} from '../../../forecast-app/shared/components/toasts/toast';
import {clientLogoSrc} from '../../../directApi';
import {hasModule} from '../../shared/util/ModuleUtil';
import {hasFeatureFlag} from '../../shared/util/FeatureUtil';
import {ClientPrimaryContactDropdown} from '../../shared/components/dropdowns/client-primary-contact-dropdown/ClientPrimaryContactDropdown';

type IsoCountryType = {value: string; label: string};

type ClientStatus = {id: string; label: string; standing: string};

type Contact = {id: string; name: string};

type EditClientDetailsModalProps = {
	clientId: number;
	closeModal: () => void;
	viewer: EditClientDetailsModal_viewer;
};

enum LogoState {
	NO_CHANGE = 'NO_CHANGE',
	UPDATE = 'UPDATE',
	DELETE = 'DELETE',
}

const hasAddressData = (client: NonNullable<EditClientDetailsModal_viewer['company']>['client']): boolean =>
	!!(client?.street || client?.zip || client?.city || client?.country || client?.region || client?.vat);

export const EditClientDetailsModal = ({clientId, viewer, closeModal}: EditClientDetailsModalProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {client} = viewer?.company || {};
	const clientStatuses: ClientStatus[] =
		viewer?.company?.clientStatuses?.edges?.flatMap(e => (e?.node ? [e.node as ClientStatus] : [])) ?? [];
	const changeAvatarButtonRef = useRef<HTMLInputElement>(null);

	const [name, setName] = useState<string>(client?.name || '');
	const [website, setWebsite] = useState<string>(client?.website || '');
	const [region, setRegion] = useState<string>(client?.region || '');
	const [statusId, setStatusId] = useState<string | null>(client?.status?.id ?? null);
	const [primaryContact, setPrimaryContact] = useState<Contact | null>(
		client?.primaryContact
			? {
					id: client.primaryContact.id,
					name: [client.primaryContact.firstName, client.primaryContact.lastName].filter(Boolean).join(' '),
			  }
			: null
	);
	const [street, setStreet] = useState<string>(client?.street || '');
	const [zip, setZip] = useState<string>(client?.zip || '');
	const [city, setCity] = useState<string>(client?.city || '');
	const [vat, setVat] = useState<string>(client?.vat || '');
	const [country, setCountry] = useState<IsoCountryType | null>(
		isoCountries.find((c: IsoCountryType) => c.value === client?.country) || null
	);
	const [isAddressExpanded, setIsAddressExpanded] = useState<boolean>(hasAddressData(client ?? null));

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [logoState, setLogoState] = useState<LogoState>(LogoState.NO_CHANGE);
	const [logo, setLogo] = useState<string>(
		client?.logoId ? clientLogoSrc(client.logoId) : getDefaultMediumClientLogo(client?.logoDefaultId)
	);

	const selectedStatus = clientStatuses.find(s => s.id === statusId) ?? null;

	const onClickChangeAvatar = () => changeAvatarButtonRef.current?.click();

	const onChangeAvatar = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;
		Util.confirmFilePreviewType(file, res => {
			if (res) {
				setLogoState(LogoState.UPDATE);
				setSelectedFile(file);
				setLogo(URL.createObjectURL(file));
			} else {
				closeModal();
				showModal({
					type: MODAL_TYPE.GENERIC,
					content: (
						<div className="default-warning-modal">
							<Warning messageId="warning_modal.profile_picture_wrong_format_title" />
							<div className="warning-part-2">
								{formatMessage({id: 'warning_modal.profile_picture_wrong_format_info'})}
							</div>
						</div>
					),
					buttons: [
						{
							text: formatMessage({id: 'common.cancel'}),
							style: BUTTON_STYLE.FILLED,
							color: BUTTON_COLOR.WHITE,
						},
					],
				});
			}
		});
	};

	const onClickRemoveAvatar = () => {
		setLogoState(LogoState.DELETE);
		setSelectedFile(null);
		setLogo(getDefaultMediumClientLogo(client?.logoDefaultId));
	};

	const hasNewClientsFeature = hasFeatureFlag('new_clients_feature');

	const onClickSave = () => {
		const mutationBody = {
			id: clientId,
			name,
			...(hasNewClientsFeature
				? {website, region, statusId: statusId || undefined, primaryContactId: primaryContact?.id || null}
				: {}),
			street,
			zip,
			city,
			vat,
			country: country?.value || '',
			...(logoState === LogoState.DELETE ? {logoId: 'delete'} : {}),
		};

		const onSuccess = () => {
			showChangesSavedToast(formatMessage);
			closeModal();
		};

		if (logoState === LogoState.UPDATE) {
			Util.CommitMutation(UpdateClientMutation, mutationBody, onSuccess, true, {
				id: clientId,
				file: selectedFile,
			});
		} else {
			Util.CommitMutation(UpdateClientMutation, mutationBody, onSuccess);
		}
	};

	const isRemoveLogoDisabled = (!client?.logoId && logoState !== LogoState.UPDATE) || logoState === LogoState.DELETE;
	const isUploadingNew = logoState === LogoState.DELETE || (!client?.logoId && logoState !== LogoState.UPDATE);

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="clients.edit-client" defaultMessage="Edit Client" />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					{/* Client Name */}
					<TextField
						label={formatMessage({id: 'clients.client-name'})}
						value={name}
						onValueChange={setName}
						size="large"
						width="flex"
					/>

					{/* Status */}
					{hasNewClientsFeature && (
						<FlexColumn gap={'s'}>
							<Label>
								<FormattedMessage id="common.status" />
							</Label>
							<DropdownList>
								<DropdownList.Trigger>
									<ButtonDropdownField>
										<Text>{selectedStatus?.label ?? ''}</Text>
									</ButtonDropdownField>
								</DropdownList.Trigger>
								<DropdownList.Content align={'start'}>
									{clientStatuses.map(status => (
										<DropdownList.Item key={status.id} onClick={() => setStatusId(status.id)}>
											<FlexRow>
												<Text>{status.label}</Text>
												{status.id === statusId && <Icon icon={'iconTick'} />}
											</FlexRow>
										</DropdownList.Item>
									))}
								</DropdownList.Content>
							</DropdownList>
						</FlexColumn>
					)}

					{/* Client Contact — new clients feature + contacts module */}
					{hasNewClientsFeature && hasModule(MODULE_TYPES.NEW_CLIENT_CONTACTS_FEATURE) && (
						<ClientPrimaryContactDropdown
							clientId={String(clientId)}
							value={primaryContact}
							onChange={setPrimaryContact}
						/>
					)}

					{/* Website */}
					{hasNewClientsFeature && (
						<TextField
							label={formatMessage({id: 'clients.website'})}
							value={website}
							onValueChange={setWebsite}
							size="large"
							width="flex"
						/>
					)}

					{/* View more / View less toggle */}
					<FlexRow gap={'s'} alignItems={'center'}>
						<div style={{flex: 1, height: 1, background: 'var(--border-decorative)'}} />
						<Button emphasis="low" onPress={() => setIsAddressExpanded(prev => !prev)}>
							{formatMessage({
								id: isAddressExpanded ? 'clients.view-less' : 'clients.view-more',
								defaultMessage: isAddressExpanded ? 'View Less' : 'View More',
							})}
						</Button>
						<div style={{flex: 1, height: 1, background: 'var(--border-decorative)'}} />
					</FlexRow>

					{/* Address section */}
					{isAddressExpanded && (
						<FlexColumn gap={'l'}>
							<TextField
								label={formatMessage({id: 'clients.street'})}
								value={street}
								onValueChange={setStreet}
								size="large"
								width="flex"
							/>
							<FlexRow gap={'l'}>
								<TextField
									label={formatMessage({id: 'clients.city'})}
									value={city}
									onValueChange={setCity}
									size="large"
									width="flex"
								/>
								<TextField
									label={formatMessage({id: 'clients.zip'})}
									value={zip}
									onValueChange={setZip}
									size="large"
									width="flex"
								/>
							</FlexRow>
							{hasNewClientsFeature && (
								<TextField
									label={formatMessage({id: 'clients.region'})}
									value={region}
									onValueChange={setRegion}
									size="large"
									width="flex"
								/>
							)}
							<FlexColumn gap={'s'}>
								<Label>
									<FormattedMessage id="clients.country" />
								</Label>
								<DropdownList>
									<DropdownList.Trigger>
										<ButtonDropdownField>
											<Text>{country?.label ?? ''}</Text>
										</ButtonDropdownField>
									</DropdownList.Trigger>
									<DropdownList.Content align={'start'}>
										{isoCountries.map((c: IsoCountryType) => (
											<DropdownList.Item key={c.value} onClick={() => setCountry(c)}>
												<FlexRow>
													<Text>{c.label}</Text>
													{c.value === country?.value && <Icon icon={'iconTick'} />}
												</FlexRow>
											</DropdownList.Item>
										))}
									</DropdownList.Content>
								</DropdownList>
							</FlexColumn>
							<FlexColumn gap={'s'}>
								<FlexRow gap={'xs'} alignItems={'center'}>
									<Label>
										<FormattedMessage id="clients.tax-code" />
									</Label>
									<IconTooltip>
										<IconTooltip.Item
											title={formatMessage({
												id: 'clients.tax-code-tooltip',
												defaultMessage: 'Tax / VAT code',
											})}
										/>
									</IconTooltip>
								</FlexRow>
								<TextField value={vat} onValueChange={setVat} size="large" width="flex" />
							</FlexColumn>
						</FlexColumn>
					)}

					{/* Logo */}
					<FlexRow gap={'l'} alignItems={'center'}>
						<FlexContainer>
							<img
								crossOrigin="use-credentials"
								data-private
								alt="client-logo"
								src={logo}
								width="96"
								height="96"
							/>
						</FlexContainer>
						<FlexColumn>
							<input
								type="file"
								style={{display: 'none'}}
								onChange={onChangeAvatar}
								ref={changeAvatarButtonRef}
								accept=".jpg,.jpeg,.png,.gif"
							/>
							<Button emphasis="low" onPress={onClickChangeAvatar}>
								{formatMessage({id: isUploadingNew ? 'clients.upload-logo' : 'clients.update-logo'})}
							</Button>
							{!isRemoveLogoDisabled && (
								<Button emphasis="low" onPress={onClickRemoveAvatar}>
									{formatMessage({id: 'clients.remove-logo', defaultMessage: 'Remove Logo'})}
								</Button>
							)}
						</FlexColumn>
					</FlexRow>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={onClickSave}>
					{formatMessage({id: 'clients.save-client', defaultMessage: 'Save Client'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const EditClientDetailsModalQuery = graphql`
	query EditClientDetailsModal_Query($clientId: String) {
		viewer {
			actualPersonId
			component(name: "edit_client_details_modal")
			...EditClientDetailsModal_viewer @arguments(clientId: $clientId)
		}
	}
`;

export default createFragmentContainer(EditClientDetailsModal, {
	viewer: graphql`
		fragment EditClientDetailsModal_viewer on Viewer @argumentDefinitions(clientId: {type: "String"}) {
			id
			availableFeatureFlags {
				key
			}
			company {
				id
				tier
				clientStatuses(first: 100) {
					edges {
						node {
							id
							label
							standing
						}
					}
				}
				client(id: $clientId) {
					id
					name
					street
					zip
					city
					vat
					country
					region
					website
					notes
					logoId
					logoDefaultId
					status {
						id
						label
						standing
					}
					primaryContact {
						id
						firstName
						lastName
					}
				}
			}
		}
	`,
});
